import { ArrowDown, ArrowUp, ChevronsUp, Minus, Paperclip } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/primitives';
import type { Priority, StatusCategory } from '@/lib/types';
import { cn } from '@/lib/utils';

export const PRIORITY: Record<Priority, { label: string; className: string; icon: typeof Minus }> = {
  LOW: { label: 'Niedrig', className: 'text-muted-foreground', icon: ArrowDown },
  NORMAL: { label: 'Normal', className: 'text-muted-foreground', icon: Minus },
  HIGH: { label: 'Hoch', className: 'text-amber-600 dark:text-amber-400', icon: ArrowUp },
  URGENT: { label: 'Dringend', className: 'text-destructive', icon: ChevronsUp },
};

export const CATEGORY_LABELS: Record<StatusCategory, string> = {
  NEW: 'Neu',
  OPEN: 'Offen',
  WAITING: 'Wartend',
  RESOLVED: 'Gelöst',
  CLOSED: 'Geschlossen',
};

export function StatusBadge({ name, color }: { name: string; color: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium">
      <span className="size-2 rounded-full" style={{ background: color }} />
      {name}
    </span>
  );
}

export function PriorityLabel({ priority, compact }: { priority: Priority; compact?: boolean }) {
  const p = PRIORITY[priority];
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-medium', p.className)} title={p.label}>
      <p.icon className="size-3.5" />
      {!compact && p.label}
    </span>
  );
}

export function AreaBadge({ name, color }: { name: string; color: string }) {
  return (
    <Badge variant="outline" className="gap-1.5">
      <span className="size-1.5 rounded-full" style={{ background: color }} />
      {name}
    </Badge>
  );
}

export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: { value: T; label: ReactNode }[] }) {
  return (
    <div className="flex gap-1 overflow-x-auto border-b [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist">
      {items.map((it) => (
        <button
          key={it.value}
          role="tab"
          aria-selected={value === it.value}
          onClick={() => onChange(it.value)}
          className={cn(
            '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
            value === it.value ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

export function UploadButton({ onFile, loading, label = 'Datei anhängen' }: { onFile: (f: File) => void; loading?: boolean; label?: string }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        className="hidden"
        accept="image/*,application/pdf,.docx,.xlsx,.pptx,.odt,.ods,.zip,.txt,.log,.csv,.eml"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
      <Button type="button" variant="outline" size="sm" loading={loading} onClick={() => input.current?.click()}>
        <Paperclip /> {label}
      </Button>
    </>
  );
}

export function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function formatMinutes(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? `${h} h ${m ? `${m} min` : ''}`.trim() : `${m} min`;
}

/** Lesbarer Text für Einträge im Bearbeitungsverlauf. */
export function describeEvent(type: string, data: Record<string, unknown> | null, names: { agents: Map<string, string>; teams: Map<string, string> }): string {
  const d = data ?? {};
  switch (type) {
    case 'created':
      return 'hat das Ticket erstellt';
    case 'status_changed':
      return `hat den Status von „${d.from}“ auf „${d.to}“ geändert${d.reason === 'customer_reply' ? ' (Kundenantwort)' : ''}`;
    case 'assigned':
      return `hat das Ticket ${names.agents.get(String(d.assigneeId)) ?? 'einem Mitarbeiter'} zugewiesen`;
    case 'unassigned':
      return 'hat die Zuweisung entfernt';
    case 'team_changed':
      return d.teamId ? `hat das Team ${names.teams.get(String(d.teamId)) ?? ''} gesetzt` : 'hat das Team entfernt';
    case 'priority_changed':
      return `hat die Priorität auf „${PRIORITY[d.to as Priority]?.label ?? d.to}“ gesetzt`;
    case 'area_changed':
      return `hat den Bereich auf „${d.to}“ geändert`;
    case 'category_changed':
      return 'hat die Kategorie geändert';
    case 'subject_changed':
      return 'hat den Betreff geändert';
    case 'description_changed':
      return 'hat die Beschreibung geändert';
    case 'customer_changed':
      return 'hat Kunde bzw. Ansprechpartner geändert';
    case 'due_changed':
      return d.dueAt ? 'hat die Fälligkeit gesetzt' : 'hat die Fälligkeit entfernt';
    case 'follow_up_changed':
      return d.followUpAt ? 'hat eine Wiedervorlage gesetzt' : 'hat die Wiedervorlage entfernt';
    case 'attachment_added':
      return `hat „${d.name}“ angehängt${d.visibility === 'INTERNAL' ? ' (intern)' : ''}`;
    case 'time_logged':
      return `hat ${formatMinutes(Number(d.minutes))} gebucht`;
    case 'time_deleted':
      return 'hat eine Zeitbuchung gelöscht';
    case 'linked':
      return `hat mit ${d.number} verknüpft`;
    case 'unlinked':
      return 'hat eine Verknüpfung entfernt';
    case 'checklist_added':
      return `hat die Checkliste „${d.form}“ ausgefüllt`;
    case 'monitoring_problem':
      return `meldet „${d.problem}“ auf ${d.host}`;
    case 'monitoring_recovered':
      return `meldet „${d.problem}“ auf ${d.host} als behoben`;
    case 'device_added':
      return `hat das Gerät ${d.device} verknüpft`;
    case 'device_removed':
      return `hat das Gerät ${d.device} gelöst`;
    case 'email_received':
      return `E-Mail von ${d.from} empfangen`;
    case 'appointment_scheduled':
      return `hat den Termin „${d.title}“ am ${new Date(String(d.startAt)).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })} geplant`;
    case 'appointment_rescheduled':
      return `hat den Termin „${d.title}“ auf ${new Date(String(d.startAt)).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })} verschoben`;
    case 'appointment_cancelled':
      return `hat den Termin „${d.title}“ abgesagt`;
    case 'repair_created':
      return `hat die Reparatur ${d.number} angelegt`;
    case 'billing_released':
      return `hat den Vorgang zur Abrechnung freigegeben${d.note ? ` („${d.note}“)` : ''}`;
    case 'billing_withdrawn':
      return `hat die Abrechnungsfreigabe zurückgenommen${d.note ? ` (${d.note})` : ''}`;
    case 'billing_returned':
      return `hat den Vorgang mit Rückfrage zurückgegeben: „${d.note}“`;
    case 'billing_transferred':
      return `hat den Vorgang in Powerbird übernommen${d.reference ? ` (Beleg ${d.reference})` : ''}`;
    case 'billing_not_billable':
      return `hat den Vorgang ohne Rechnung abgeschlossen${d.note ? ` (${d.note})` : ''}`;
    case 'billing_change_requested':
      return `hat eine Änderung beantragt: „${d.note}“`;
    case 'billing_change_approved':
      return `hat den Änderungsantrag genehmigt${d.note ? ` (${d.note})` : ''} – wieder bearbeitbar`;
    case 'billing_change_rejected':
      return `hat den Änderungsantrag abgelehnt${d.note ? `: ${d.note}` : ''}`;
    case 'billing_reopened':
      return 'hat die Abrechnung wieder geöffnet';
    default:
      return type;
  }
}
